import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

// =============================================================================
// 2-TIER SUBSCRIPTION ARCHITECTURE (Starter + Pro)
// =============================================================================
// Simplified model aligned with RevenueCat + Apple IAP
// Products: Pro Monthly / Pro Yearly / Pro Lifetime
// Entitlement: "pro" (configured in RevenueCat dashboard)
// =============================================================================

// Subscription tier definitions
const DEFAULT_SUBSCRIPTION_TIERS = {
    STARTER: {
        level: 0,
        name: 'Starter',
        tagline: 'Free Forever',
        color: 'gray',
        icon: '🆓',
        monthlyPrice: 0,
        yearlyPrice: 0,
        monthlyPriceId: null,
        yearlyPriceId: null
    },
    PRO: {
        level: 1,
        name: 'Pro',
        tagline: 'All Access',
        color: 'orange',
        icon: '🔥',
        monthlyPrice: 9.99,
        yearlyPrice: 49.99,
        lifetimePrice: 149.99,
        savings: '58%',
        badge: 'BEST VALUE',
        trial: true,
        trialDays: 7,
        monthlyPriceId: 'calobite_pro_monthly',
        yearlyPriceId: 'calobite_pro_yearly',
        lifetimePriceId: 'calobite_pro_lifetime'
    }
};

// Export tiers

// Safe accessor — prevents crash if currentTier is undefined/unknown during init
const getTL = (tier) => TIER_LIMITS[tier] || TIER_LIMITS.STARTER;

export let SUBSCRIPTION_TIERS = { ...DEFAULT_SUBSCRIPTION_TIERS };

// Feature limits per tier - strategic gating for conversion
const TIER_LIMITS = {
    STARTER: {
        // Scanning/Meal Generation - 28 TOTAL (lifetime limit)
        totalScans: 28,
        dailyScans: Infinity,
        aiRefinement: false,
        totalRefinements: 28,

        // Exercise Generation - 28 TOTAL (lifetime limit)
        totalExerciseGeneration: 28,
        dailyExerciseGeneration: Infinity,

        // Dashboard Logging - 28 TOTAL each (lifetime limit)
        totalMealLogs: 28,
        totalExerciseLogs: 28,
        maxMealLogsPerDay: Infinity,
        maxExerciseLogsPerDay: Infinity,

        // Cuisine Fusion - 28 TOTAL
        canUseFusion: true,
        totalFusionLimit: 28,
        maxFusionInstances: 28,

        // Dashboard Display
        macroRings: false,
        weightTrendChart: false,
        bodyStatusIndicator: true,

        // Premium Features (LOCKED)
        metabolicTable: false,
        hourlyBurnTimeline: false,
        realTimeBodyAnalysis: false,

        // AI Features - 28 TOTAL
        aiSchedule: false,
        aiWorkouts: true,
        aiMealSuggestions: true,
        aiCoaching: false,

        // Diet Features (LOCKED)
        dietDetection: false,
        aiDietAnalysis: false,
        dietCompliance: false,

        // Journey Tracking (LOCKED)
        journeyTracking: false,

        // Recipes
        recipesPerMonth: 10,

        // Profile
        verifiedBadge: false,

        // Experience
        removeAds: false,
        prioritySupport: false,

        // Referrals - CANNOT EARN
        canEarnReferrals: false,
        rewardMultiplier: 0,

        // AI Generation - 28 TOTAL (lifetime limit)
        weeklyAIGenerations: Infinity,
        totalAIGenerations: 28,

        // Favorites & Saving - NOT ALLOWED
        canSaveExercises: false,
        maxSavedExercises: 0,
        canCreateRoutine: false,
        maxRoutines: 0,
        canSaveMeals: false,
        maxSavedMeals: 0
    },
    PRO: {
        // Scanning - UNLIMITED + AI
        dailyScans: Infinity,
        aiRefinement: true,

        // Exercise Generation - UNLIMITED
        dailyExerciseGeneration: Infinity,

        // Dashboard Logging - Unlimited
        maxMealLogsPerDay: Infinity,
        maxExerciseLogsPerDay: Infinity,

        // Dashboard - FULL ACCESS
        macroRings: true,
        weightTrendChart: true,
        bodyStatusIndicator: true,
        realTimeBodyAnalysis: true,

        // Premium Features - ALL UNLOCKED
        metabolicTable: true,
        hourlyBurnTimeline: true,

        // AI Features - ALL UNLOCKED
        aiSchedule: true,
        aiWorkouts: true,
        aiMealSuggestions: true,
        aiCoaching: true,

        // Diet Features - ALL UNLOCKED
        dietDetection: true,
        aiDietAnalysis: true,
        dietCompliance: true,

        // Journey Tracking - ENABLED
        journeyTracking: true,

        // Recipes - UNLIMITED
        recipesPerMonth: Infinity,

        // Profile
        verifiedBadge: true,

        // Experience
        removeAds: true,
        prioritySupport: true,

        // Referrals - STANDARD RATE
        canEarnReferrals: true,
        rewardMultiplier: 1,

        // AI Generation - UNLIMITED
        weeklyAIGenerations: Infinity,

        // Favorites & Saving - FULL ACCESS (Unlimited)
        canSaveExercises: true,
        maxSavedExercises: Infinity,
        canCreateRoutine: true,
        maxRoutines: Infinity,
        canSaveMeals: true,
        maxSavedMeals: Infinity,
        canUseFusion: true,
        maxFusionInstances: Infinity
    }
};

// Billing product IDs (RevenueCat / App Store Connect)
export const BILLING_PRODUCTS = {
    PRO: {
        monthly: 'calobite_pro_monthly',
        yearly: 'calobite_pro_yearly',
        lifetime: 'calobite_pro_lifetime'
    }
};

export const CURRENCY = 'AUD';
export const CURRENCY_SYMBOL = '$';

// Import Supabase for database sync
import { supabase } from '../lib/supabase';
import { apiUrl } from '../config/api';
import { useUser } from './UserContext';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) throw new Error('useSubscription must be used within SubscriptionProvider');
    return context;
};

// Safe JSON parse — prevents localStorage corruption from crashing the app
const safeParse = (str, fallback) => {
    if (!str) return fallback;
    try { return JSON.parse(str); } catch { return fallback; }
};

export const SubscriptionProvider = ({ children }) => {
    // ==================== SUPER ADMIN BYPASS ====================
    // Super admin gets full ELITE access to all features without any restrictions

    // Extract user profile for dynamic SuperAdmin checks
    const { userProfile } = useUser();

    // Make isSuperAdmin reactive - recalculates when currentUserEmail changes
    const [currentUserEmail, setCurrentUserEmail] = useState(() =>
        localStorage.getItem('calobite_current_user')
    );

    // CRITICAL: isSuperAdmin is now determined by profile flag
    const isSuperAdmin = useMemo(() => {
        return userProfile?.isSuperAdmin === true;
    }, [userProfile]);

    /**
     * ⚡ CRITICAL FIX: SuperAdmin race-condition resolver
     *
     * Problem: userProfile is null on first render (async load). During that window,
     * isSuperAdmin = false, so syncFromSupabase runs as a normal user, and
     * RevenueCat may win and set tier to STARTER.
     *
     * Fix: Watch userProfile specifically. The moment isSuperAdmin becomes true
     * (even after initial render), immediately force PRO and persist it — before
     * any RevenueCat or Supabase sync can override it.
     */
    useEffect(() => {
        if (!isSuperAdmin) return;

        // Force PRO in React state (volatile memory only — NEVER persisted to localStorage)
        // The nuclear wipe on logout clears all calobite_ keys anyway, but the key principle
        // is that is_superadmin status is ONLY derived from the live DB profile, never from cache.
        setCurrentTier('PRO');

        // ⛔ SECURITY: Do NOT write tier or override to localStorage for superadmin.
        // Privilege data must not survive a session boundary. The previous session
        // would be wiped by fullNuclearLogout(), but writing them at all creates
        // a window where a partial wipe could leave PRO status behind for the next user.
        console.log('[SubscriptionContext] 👑 SuperAdmin detected — PRO forced in memory (NOT persisted to storage)');
    }, [isSuperAdmin, currentUserEmail]);

    // Listen for user changes via the custom auth event (works in Capacitor iOS)
    // and keep the interval as a secondary safety net.
    useEffect(() => {
        const handleAuthChanged = (e) => {
            const newEmail = (e?.detail?.email) || localStorage.getItem('calobite_current_user') || null;
            if (newEmail !== currentUserEmail) {
                console.log('[SubscriptionContext] ⚡ Auth event: user changed', currentUserEmail, '->', newEmail);
                setCurrentUserEmail(newEmail);
            }
        };

        // Primary: custom event (fires in Capacitor iOS, same webview)
        window.addEventListener('calobite_auth_changed', handleAuthChanged);
        // Secondary: storage event (fires across tabs in browser)
        window.addEventListener('storage', handleAuthChanged);
        // Tertiary fallback: poll every 2s for edge cases
        const interval = setInterval(() => {
            const newEmail = localStorage.getItem('calobite_current_user');
            if (newEmail !== currentUserEmail) {
                console.log('[SubscriptionContext] Poll: user changed', currentUserEmail, '->', newEmail);
                setCurrentUserEmail(newEmail);
            }
        }, 2000);

        return () => {
            window.removeEventListener('calobite_auth_changed', handleAuthChanged);
            window.removeEventListener('storage', handleAuthChanged);
            clearInterval(interval);
        };
    }, [currentUserEmail]);

    // Pricing is now static (aligned with App Store IAP product prices)
    // No Stripe API fetch needed — prices come from App Store Connect

    // Current tier - USER-SPECIFIC to prevent cross-user tier inheritance
    const [currentTier, setCurrentTier] = useState(() => {
        const email = localStorage.getItem('calobite_current_user');

        // 🔥 GOD MODE: Super admin ALWAYS gets PRO tier
        // NOTE: During initial hydration userProfile is null, so wait for useEffect to apply PRO if admin.

        // For regular users with email, ONLY check their user-specific tier
        // DO NOT fall back to global key - this was causing tier contamination
        if (email) {
            const userTierKey = `calobite_tier_${email}`;
            const saved = localStorage.getItem(userTierKey);

            if (saved) {
                // Comprehensive tier normalization — must handle ALL legacy/invalid values
                // so SUBSCRIPTION_TIERS[currentTier] never returns undefined
                const normalizeTier = (t) => {
                    const upper = (t || '').toUpperCase();
                    if (upper === 'PRO') return 'PRO';
                    if (upper === 'STARTER' || upper === 'FREE' || upper === 'CORE' || upper === 'BASIC') return 'STARTER';
                    // Legacy tiers that were removed — map to their closest equivalent
                    if (upper === 'ELITE' || upper === 'PLUS' || upper === 'AMBASSADOR') return 'PRO';
                    // Unknown or malformed — safe default
                    return 'STARTER';
                };
                return normalizeTier(saved);
            }

            // New user with email but no saved tier = STARTER
            console.log('[SubscriptionContext] New user detected - starting as STARTER:', email);
            return 'STARTER';
        }

        // No email = not logged in = STARTER
        console.log('[SubscriptionContext] No user logged in - defaulting to STARTER');
        return 'STARTER';
    });

    // 🔥 CRITICAL: When user changes, IMMEDIATELY reset to STARTER.
    // Do NOT check userProfile.isSuperAdmin here — it is STALE (still reflects
    // the previous user during the async profile reload). The Supabase sync
    // that runs next will re-apply PRO if the new user is genuinely PRO or superadmin.
    useEffect(() => {
        if (!currentUserEmail) {
            // Logged out — reset to safe default
            setCurrentTier('STARTER');
            return;
        }

        // Immediately reset to STARTER for this user before the DB sync fires.
        // This prevents the old superadmin's PRO tier from bleeding into the new session.
        const userTierKey = `calobite_tier_${currentUserEmail}`;
        const savedTier = localStorage.getItem(userTierKey);

        const normalizeTier = (t) => {
            const upper = (t || '').toUpperCase();
            if (upper === 'PRO') return 'PRO';
            if (upper === 'ELITE' || upper === 'PLUS' || upper === 'AMBASSADOR') return 'PRO';
            return 'STARTER';
        };

        if (savedTier) {
            setCurrentTier(normalizeTier(savedTier));
        } else {
            console.log('[SubscriptionContext] New user email — resetting to STARTER:', currentUserEmail);
            setCurrentTier('STARTER');
        }
    }, [currentUserEmail]);

    // ==================== PRODUCTION: SUPABASE SYNC ====================
    // Sync subscription tier from Supabase on user login
    // Database is source of truth, localStorage is just cache
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncError, setSyncError] = useState(null);

    useEffect(() => {
        const syncFromSupabase = async () => {
            // Skip sync for super admins — they always have PRO access via DB flag
            if (!currentUserEmail || isSuperAdmin) {
                return;
            }

            setSyncLoading(true);
            setSyncError(null);

            try {
                const { supabase } = await import('../lib/supabase');
                if (!supabase) {
                    console.warn('[SubscriptionContext] Supabase not available for sync');
                    return;
                }

                const { data: profile, error } = await supabase
                    .from('profiles')
                    .select('subscription_tier, subscription_expiry, is_trial, subscription_override, scans_used')
                    .eq('email', currentUserEmail.toLowerCase())
                    .single();

                if (error) {
                    console.warn('[SubscriptionContext] Supabase sync error:', error.message);
                    setSyncError('Could not sync subscription status');
                    return;
                }

                if (profile) {
                    // Database is source of truth for tier
                    // Only two tiers: STARTER and PRO
                    const dbTier = (profile.subscription_tier || 'STARTER').toUpperCase();

                    // Normalise — any paid legacy value → PRO
                    let normalizedTier = 'STARTER';
                    if (['PRO', 'PLUS', 'ELITE', 'AMBASSADOR', 'PREMIUM'].includes(dbTier)) {
                        normalizedTier = 'PRO';
                    } else if (dbTier === 'STARTER' || dbTier === 'FREE' || dbTier === 'CORE' || dbTier === 'BASIC') {
                        normalizedTier = 'STARTER';
                    }

                    if (normalizedTier !== currentTier) {
                        console.log('[SubscriptionContext] 🔄 Syncing tier from Supabase:', currentTier, '->', normalizedTier);
                        setCurrentTier(normalizedTier);
                    }

                    // Sync expiry date if present
                    if (profile.subscription_expiry) {
                        setExpiryDate(new Date(profile.subscription_expiry));
                    }

                    // Save override flag for RevenueCat check bypass
                    if (profile.subscription_override) {
                        localStorage.setItem(`calobite_override_${currentUserEmail}`, 'true');
                    } else {
                        localStorage.removeItem(`calobite_override_${currentUserEmail}`);
                    }

                    // ⚡ Seed totalScansUsed from Supabase (source of truth)
                    // Take the MAX of DB and localStorage to avoid rewinding counter
                    if (typeof profile.scans_used === 'number' && profile.scans_used > 0) {
                        const localCount = parseInt(localStorage.getItem('calobite_total_scans_used') || '0', 10);
                        const authoritative = Math.max(profile.scans_used, localCount);
                        setTotalScansUsed(authoritative);
                        localStorage.setItem('calobite_total_scans_used', authoritative.toString());
                    }

                    console.log('[SubscriptionContext] ✅ Synced from Supabase:', { tier: normalizedTier, expiry: profile.subscription_expiry, override: profile.subscription_override, scansUsed: profile.scans_used });
                }
            } catch (err) {
                console.error('[SubscriptionContext] Sync error:', err);
                setSyncError('Failed to sync subscription');
            } finally {
                setSyncLoading(false);
            }
        };

        // Delay sync slightly to let app initialize
        const timeout = setTimeout(syncFromSupabase, 500);
        return () => clearTimeout(timeout);
    }, [currentUserEmail, currentTier]);

    // ── RevenueCat native entitlement event listener ───────────────────────
    // NOTE: This listener is registered later in the component (after refreshTierFromRevenueCat
    // is defined as a useCallback) to avoid referencing an undefined function.
    // See the useEffect near refreshTierFromRevenueCat below.

    // Subscription expiry date (for paid tiers)
    const [expiryDate, setExpiryDate] = useState(() => {
        const saved = localStorage.getItem('calobite_subscription_expiry');
        return saved ? new Date(saved) : null;
    });

    // Trial tracking
    const [trialStartDate, setTrialStartDate] = useState(() => {
        const saved = localStorage.getItem('calobite_trial_start');
        return saved ? new Date(saved) : null;
    });

    const [trialUsed, setTrialUsed] = useState(() => {
        return localStorage.getItem('calobite_trial_used') === 'true';
    });

    // Payment method tracking (for trial and subscriptions)
    const [hasPaymentMethod, setHasPaymentMethod] = useState(() => {
        return localStorage.getItem('calobite_has_payment_method') === 'true';
    });

    // Scheduled downgrade (takes effect at end of billing cycle)
    const [scheduledDowngrade, setScheduledDowngrade] = useState(() => {
        const saved = localStorage.getItem('calobite_scheduled_downgrade');
        return safeParse(saved, null); // { tier: 'STARTER', effectiveDate: ISO string }
    });

    // Trial notification tracking
    const [trialNotifications, setTrialNotifications] = useState(() => {
        const saved = localStorage.getItem('calobite_trial_notifications');
        return safeParse(saved, { threeDaySent: false, oneDaySent: false });
    });

    // Daily scan tracking
    const [scansToday, setScansToday] = useState(() => {
        const today = new Date().toDateString();
        const saved = localStorage.getItem('calobite_scans_today');
        if (saved) {
            const data = safeParse(saved, {});
            if (data.date === today) return data.count;
        }
        return 0;
    });

    // Total scan tracking (for Starter tier - 28 total scans ever)
    const [totalScansUsed, setTotalScansUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_scans_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Total exercise generations used (for Starter tier - 28 total)
    const [totalExerciseGenerationsUsed, setTotalExerciseGenerationsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_exercise_gen_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Total AI generations used (for Starter tier - 28 total)
    const [totalAIGenerationsUsed, setTotalAIGenerationsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_ai_gen_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Daily meal log tracking
    const [mealLogsToday, setMealLogsToday] = useState(() => {
        const today = new Date().toDateString();
        const saved = localStorage.getItem('calobite_meal_logs_today');
        if (saved) {
            const data = safeParse(saved, {});
            if (data.date === today) return data.count;
        }
        return 0;
    });

    // Daily exercise log tracking
    const [exerciseLogsToday, setExerciseLogsToday] = useState(() => {
        const today = new Date().toDateString();
        const saved = localStorage.getItem('calobite_exercise_logs_today');
        if (saved) {
            const data = safeParse(saved, {});
            if (data.date === today) return data.count;
        }
        return 0;
    });

    // Total meal logs used (for Starter tier - 28 total)
    const [totalMealLogsUsed, setTotalMealLogsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_meal_logs_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Total exercise logs used (for Starter tier - 28 total)
    const [totalExerciseLogsUsed, setTotalExerciseLogsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_exercise_logs_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Total fusion uses (for Starter tier - 28 total)
    const [totalFusionsUsed, setTotalFusionsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_fusions_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Total refinements used (for Starter tier - 28 total)
    const [totalRefinementsUsed, setTotalRefinementsUsed] = useState(() => {
        const saved = localStorage.getItem('calobite_total_refinements_used');
        return saved ? parseInt(saved, 10) : 0;
    });

    // Monthly recipe tracking
    const [recipesThisMonth, setRecipesThisMonth] = useState(() => {
        const month = new Date().toISOString().slice(0, 7);
        const saved = localStorage.getItem('calobite_recipes_month');
        if (saved) {
            const data = safeParse(saved, null);
            if (data.month === month) return data.count;
        }
        return 0;
    });

    // Billing cycle: weekly | quarterly | yearly
    const [billingCycle, setBillingCycle] = useState(() => {
        const saved = localStorage.getItem('calobite_billing_cycle');
        // Normalise: drop legacy 'monthly' → 'yearly'
        const validCycles = ['weekly', 'quarterly', 'yearly'];
        return validCycles.includes(saved) ? saved : 'yearly';
    });

    // Referral tracking
    const [referredBy, setReferredBy] = useState(() => {
        return localStorage.getItem('calobite_referred_by') || null;
    });

    const [activeReferrals, setActiveReferrals] = useState(() => {
        const saved = localStorage.getItem('calobite_active_referrals');
        return saved ? parseInt(saved, 10) : 0;
    });

    const [referralEarnings, setReferralEarnings] = useState(() => {
        const saved = localStorage.getItem('calobite_referral_earnings');
        return saved ? parseFloat(saved) : 0;
    });

    const [pendingEarnings, setPendingEarnings] = useState(() => {
        const saved = localStorage.getItem('calobite_pending_earnings');
        return saved ? parseFloat(saved) : 0;
    });

    // User's unique referral code (loaded from referral service)
    // CRITICAL: Reset to null when user changes to prevent cross-user contamination
    const [userReferralCode, setUserReferralCode] = useState(null);

    // Load referral code from Supabase when user changes
    // CRITICAL: Always fetch fresh from DB, don't trust localStorage cache
    useEffect(() => {
        // Reset referral code immediately when user changes
        setUserReferralCode(null);

        // Clear the localStorage cache when user changes to prevent contamination
        localStorage.removeItem('calobite_user_referral_code');

        if (!currentUserEmail) {
            console.log('[Referral] No user logged in, skipping referral code load');
            return;
        }

        const loadReferralCode = async () => {
            try {
                console.log('[Referral] Loading referral code for user:', currentUserEmail);

                // Dynamic import to avoid circular dependency
                const { getReferralCode, fetchEarningsFromAPI } = await import('../services/referralService');

                // Get user ID from Supabase
                if (!supabase) {
                    console.warn('[Referral] Supabase not available');
                    return;
                }

                const { data: profile, error } = await supabase
                    .from('profiles')
                    .select('id')
                    .eq('email', currentUserEmail.toLowerCase())
                    .limit(1)
                    .maybeSingle();

                if (error || !profile?.id) {
                    console.warn('[Referral] Could not get user profile:', error?.message);
                    return;
                }

                // Try to get existing code from Supabase (anon read — allowed by RLS)
                let code = await getReferralCode(profile.id);

                if (!code) {
                    // Client-side INSERT is blocked by RLS — route through backend API
                    // which uses service_role key and auto-generates if missing
                    console.log('[Referral] No code found, fetching via backend (service role) for:', currentUserEmail);
                    const earningsData = await fetchEarningsFromAPI(profile.id);
                    code = earningsData?.referralCode || null;
                }

                if (code) {
                    console.log('[Referral] ✅ Loaded referral code for', currentUserEmail, ':', code);
                    setUserReferralCode(code);
                    // Cache for this session only
                    localStorage.setItem('calobite_user_referral_code', code);
                }
            } catch (err) {
                console.warn('[Referral] Failed to load referral code:', err);
            }
        };

        // Delay slightly to let auth settle
        const timeout = setTimeout(loadReferralCode, 300);
        return () => clearTimeout(timeout);
    }, [currentUserEmail]); // Re-run when user changes

    // Applied referral/discount code (15% regular, 25% ambassador)
    const [appliedReferralCode, setAppliedReferralCode] = useState(() => {
        return localStorage.getItem('calobite_applied_referral_code') || null;
    });

    const [referralDiscountPercent, setReferralDiscountPercent] = useState(() => {
        const saved = localStorage.getItem('calobite_referral_discount_percent');
        return saved ? parseFloat(saved) : 0;
    });

    // Check if a code is an ambassador code (AMB_ prefix = 25% discount)
    const isAmbassadorCode = useCallback((code) => {
        if (!code) return false;
        return code.toUpperCase().startsWith('AMB_') || code.toUpperCase().startsWith('AMB');
    }, []);

    // Apply a referral code as discount
    const applyReferralCode = useCallback((code) => {
        if (!code || code.trim().length < 5) return { success: false, message: 'Invalid code' };

        const cleanCode = code.trim().toUpperCase();
        const isAmbassador = isAmbassadorCode(cleanCode);
        const discountPercent = isAmbassador ? 0.25 : 0.10; // 25% ambassador, 10% regular

        setAppliedReferralCode(cleanCode);
        setReferralDiscountPercent(discountPercent);
        localStorage.setItem('calobite_applied_referral_code', cleanCode);
        localStorage.setItem('calobite_referral_discount_percent', discountPercent.toString());

        // Also track who referred this user
        if (!referredBy) {
            setReferredBy(cleanCode);
            localStorage.setItem('calobite_referred_by', cleanCode);

            // Sync with RevenueCat
            import('../services/iapService').then(({ default: iap }) => {
                if (iap?.setIAPUserAttribute) {
                    iap.setIAPUserAttribute({ referred_by: cleanCode });
                }
            }).catch(err => console.warn('[IAP] Failed to load iapService for referral sync:', err));
        }

        return {
            success: true,
            discount: discountPercent,
            isAmbassador,
            message: isAmbassador
                ? `🎉 Ambassador code applied! 25% OFF your subscription!`
                : `🎁 Referral code applied! 10% OFF your subscription!`
        };
    }, [isAmbassadorCode, referredBy]);

    // Calculate discounted price for a tier
    const calculateDiscountedPrice = useCallback((tier, cycle) => {
        const tierInfo = SUBSCRIPTION_TIERS[tier];
        if (!tierInfo) return 0;

        const basePrice = cycle === 'yearly' ? tierInfo.yearlyPrice : tierInfo.monthlyPrice;
        const discountedPrice = basePrice * (1 - referralDiscountPercent);

        return {
            originalPrice: basePrice,
            discountedPrice: Math.round(discountedPrice * 100) / 100,
            savings: Math.round(basePrice * referralDiscountPercent * 100) / 100,
            discountPercent: referralDiscountPercent * 100
        };
    }, [referralDiscountPercent]);

    // Calculate referrals needed to offset subscription cost
    const calculateReferralsToZero = useCallback((tier, cycle) => {
        // PRO pricing: weekly $4.99, quarterly $11.99, yearly $29.99
        const PRICES = { weekly: 4.99, quarterly: 11.99, yearly: 29.99 };
        const price = PRICES[cycle] || PRICES.yearly;
        const cashbackPerReferral = 30; // $30 per converted referral for PRO users

        return {
            subscriptionCost: price,
            cashbackPerReferral,
            referralsNeeded: Math.ceil(price / cashbackPerReferral),
            message: `Refer ${Math.ceil(price / cashbackPerReferral)} friends and your Pro is FREE!`
        };
    }, []);

    // Weekly AI generation tracking (resets each week)
    const getWeekKey = () => {
        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
        return `${now.getFullYear()}-W${weekNumber}`;
    };

    const [aiGenerationsThisWeek, setAiGenerationsThisWeek] = useState(() => {
        const currentWeek = getWeekKey();
        const saved = localStorage.getItem('calobite_ai_generations_week');
        if (saved) {
            const data = safeParse(saved, null);
            if (data.week === currentWeek) return data.count;
        }
        return 0;
    });

    // ==================== PERSISTENCE ====================

    // Save tier to BOTH global and user-specific storage
    // CRITICAL: Do NOT save super admin's tier to global key (causes contamination)
    useEffect(() => {
        // Only write to global key if NOT super admin
        // This prevents super admin's ELITE from leaking to new users
        if (!isSuperAdmin) {
            localStorage.setItem('calobite_subscription_tier', currentTier);
        }

        // Always save user-specific tier
        if (currentUserEmail) {
            localStorage.setItem(`calobite_tier_${currentUserEmail}`, currentTier);
        }
    }, [currentTier, currentUserEmail]);

    useEffect(() => {
        if (expiryDate) {
            localStorage.setItem('calobite_subscription_expiry', expiryDate.toISOString());
        }
    }, [expiryDate]);

    useEffect(() => {
        if (trialStartDate) {
            localStorage.setItem('calobite_trial_start', trialStartDate.toISOString());
        }
    }, [trialStartDate]);

    useEffect(() => {
        localStorage.setItem('calobite_trial_used', trialUsed.toString());
    }, [trialUsed]);

    useEffect(() => {
        localStorage.setItem('calobite_billing_cycle', billingCycle);
    }, [billingCycle]);

    useEffect(() => {
        const today = new Date().toDateString();
        localStorage.setItem('calobite_scans_today', JSON.stringify({ date: today, count: scansToday }));
    }, [scansToday]);

    useEffect(() => {
        const month = new Date().toISOString().slice(0, 7);
        localStorage.setItem('calobite_recipes_month', JSON.stringify({ month, count: recipesThisMonth }));
    }, [recipesThisMonth]);

    useEffect(() => {
        if (referredBy) localStorage.setItem('calobite_referred_by', referredBy);
    }, [referredBy]);

    useEffect(() => {
        localStorage.setItem('calobite_active_referrals', activeReferrals.toString());
    }, [activeReferrals]);

    useEffect(() => {
        localStorage.setItem('calobite_referral_earnings', referralEarnings.toString());
    }, [referralEarnings]);

    useEffect(() => {
        localStorage.setItem('calobite_pending_earnings', pendingEarnings.toString());
    }, [pendingEarnings]);

    useEffect(() => {
        const currentWeek = getWeekKey();
        localStorage.setItem('calobite_ai_generations_week', JSON.stringify({ week: currentWeek, count: aiGenerationsThisWeek }));
    }, [aiGenerationsThisWeek]);

    // Persist daily meal log count
    useEffect(() => {
        const today = new Date().toDateString();
        localStorage.setItem('calobite_meal_logs_today', JSON.stringify({ date: today, count: mealLogsToday }));
    }, [mealLogsToday]);

    // Persist daily exercise log count
    useEffect(() => {
        const today = new Date().toDateString();
        localStorage.setItem('calobite_exercise_logs_today', JSON.stringify({ date: today, count: exerciseLogsToday }));
    }, [exerciseLogsToday]);

    // Persist payment method state
    useEffect(() => {
        localStorage.setItem('calobite_has_payment_method', hasPaymentMethod.toString());
    }, [hasPaymentMethod]);

    /**
     * ⚠️ SECURITY: Sync subscription status from Supabase (The Source of Truth)
     * This function fetches the REAL subscription status from the database,
     * which is only updated by the Stripe webhook.
     */
    const syncSubscriptionFromDatabase = useCallback(async (userId) => {
        // 🔥 GOD MODE: Super admin bypasses ALL subscription checks
        if (isSuperAdmin) {
            console.log('[SubscriptionContext] 🔥 SUPER ADMIN - Skipping database sync, maintaining PRO');
            setCurrentTier('PRO');
            return;
        }

        if (!supabase || !userId) {
            console.log('[SubscriptionContext] Cannot sync - no Supabase or userId');
            return;
        }

        try {
            console.log('[SubscriptionContext] Syncing subscription from database...');

            const { data: subscription, error } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', userId)
                .single();

            if (error) {
                console.warn('[SubscriptionContext] No subscription found:', error.message);
                // No subscription = STARTER (but not for super admin - already handled above)
                setCurrentTier('STARTER');
                return;
            }

            if (subscription) {
                // Map database tier to app tier
                const dbTier = subscription.plan_tier?.toUpperCase() || subscription.tier?.toUpperCase() || 'STARTER';
                const mappedTier = dbTier === 'STARTER' || dbTier === 'FREE' ? 'STARTER' :
                    dbTier === 'PRO' || dbTier === 'BASIC' ? 'PRO' :
                        dbTier === 'PRO' || dbTier === 'PRO' ? 'PRO' : 'STARTER';

                console.log(`[SubscriptionContext] Database tier: ${dbTier} -> ${mappedTier}`);

                // Only update if subscription is active
                if (subscription.status === 'active' || subscription.status === 'trialing') {
                    setCurrentTier(mappedTier);

                    if (subscription.current_period_end) {
                        setExpiryDate(new Date(subscription.current_period_end));
                    }

                    if (subscription.billing_interval === 'year') {
                        setBillingCycle('yearly');
                    } else {
                        setBillingCycle('monthly');
                    }

                    if (subscription.status === 'trialing') {
                        setTrialUsed(true);
                        if (subscription.trial_start) {
                            setTrialStartDate(new Date(subscription.trial_start));
                        }
                    }

                    console.log(`✅ [SubscriptionContext] Synced: ${mappedTier} (${subscription.status})`);
                } else {
                    // Cancelled, past_due, etc = downgrade to STARTER (except super admin)
                    if (!isSuperAdmin) {
                        console.log(`[SubscriptionContext] Subscription not active (${subscription.status}), reverting to STARTER`);
                        setCurrentTier('STARTER');
                    } else {
                        console.log('[SubscriptionContext] 🔥 SUPER ADMIN - Ignoring inactive subscription status, maintaining PRO');
                    }
                }
            }
        } catch (err) {
            console.error('[SubscriptionContext] Error syncing from database:', err);
        }
    }, []);

    // Sync subscription on mount (if we have a current user)
    useEffect(() => {
        const checkAndSync = async () => {
            // Get current user from Supabase auth
            if (supabase) {
                try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user?.id) {
                        await syncSubscriptionFromDatabase(user.id);
                    }
                } catch (err) {
                    console.warn('[SubscriptionContext] Could not get current user:', err.message);
                }
            }
        };

        // Check on mount
        checkAndSync();

        // Also check when URL contains success (after Stripe checkout)
        if (window.location.href.includes('success')) {
            console.log('[SubscriptionContext] Detected checkout success, syncing...');
            setTimeout(() => checkAndSync(), 2000); // Wait for webhook to process
        }
    }, [syncSubscriptionFromDatabase]);

    // Persist scheduled downgrade
    useEffect(() => {
        if (scheduledDowngrade) {
            localStorage.setItem('calobite_scheduled_downgrade', JSON.stringify(scheduledDowngrade));
        } else {
            localStorage.removeItem('calobite_scheduled_downgrade');
        }
    }, [scheduledDowngrade]);

    // Persist trial notifications
    useEffect(() => {
        localStorage.setItem('calobite_trial_notifications', JSON.stringify(trialNotifications));
    }, [trialNotifications]);

    // Persist total scans used — localStorage + Supabase (fixes cross-device bypass)
    useEffect(() => {
        localStorage.setItem('calobite_total_scans_used', totalScansUsed.toString());

        // Write to Supabase so scan counter is durable across devices and reinstalls
        if (totalScansUsed > 0 && currentUserEmail) {
            import('../lib/supabase').then(({ supabase }) => {
                if (!supabase) return;
                supabase
                    .from('profiles')
                    .update({ scans_used: totalScansUsed, scan_limit: 28 })
                    .eq('email', currentUserEmail.toLowerCase())
                    .then(({ error }) => {
                        if (error) console.warn('[SubscriptionContext] Failed to persist scans_used:', error.message);
                    });
            }).catch(() => { });
        }
    }, [totalScansUsed, currentUserEmail]);

    // Persist total exercise generations used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_exercise_gen_used', totalExerciseGenerationsUsed.toString());
    }, [totalExerciseGenerationsUsed]);

    // Persist total AI generations used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_ai_gen_used', totalAIGenerationsUsed.toString());
    }, [totalAIGenerationsUsed]);

    // Persist total meal logs used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_meal_logs_used', totalMealLogsUsed.toString());
    }, [totalMealLogsUsed]);

    // Persist total exercise logs used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_exercise_logs_used', totalExerciseLogsUsed.toString());
    }, [totalExerciseLogsUsed]);

    // Persist total fusions used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_fusions_used', totalFusionsUsed.toString());
    }, [totalFusionsUsed]);

    // Persist total refinements used (for Starter tier)
    useEffect(() => {
        localStorage.setItem('calobite_total_refinements_used', totalRefinementsUsed.toString());
    }, [totalRefinementsUsed]);

    // ==================== TIER INFO ====================

    const getTierInfo = useCallback(() => {
        // Super admin always gets PRO tier info
        if (isSuperAdmin) return SUBSCRIPTION_TIERS.PRO;
        // Safe fallback: if currentTier is unknown/undefined, return STARTER
        return SUBSCRIPTION_TIERS[currentTier] || SUBSCRIPTION_TIERS.STARTER;
    }, [currentTier, isSuperAdmin]);

    const getTierLimits = useCallback(() => {
        // Super admin always gets PRO tier limits (full access)
        if (isSuperAdmin) return TIER_LIMITS.PRO;
        return getTL(currentTier);
    }, [currentTier, isSuperAdmin]);

    const hasTierLevel = useCallback((requiredTier) => {
        // Super admin always has all tier levels
        if (isSuperAdmin) return true;
        return (SUBSCRIPTION_TIERS[currentTier]?.level ?? 0) >= (SUBSCRIPTION_TIERS[requiredTier]?.level ?? 0);
    }, [currentTier, isSuperAdmin]);

    // ==================== TRIAL MANAGEMENT ====================

    const isInTrial = useCallback(() => {
        if (currentTier !== 'PRO' || !trialStartDate) return false;
        const trialEnd = new Date(trialStartDate);
        trialEnd.setDate(trialEnd.getDate() + 7);
        return new Date() < trialEnd;
    }, [currentTier, trialStartDate]);

    const getTrialDaysRemaining = useCallback(() => {
        if (!isInTrial()) return 0;
        const trialEnd = new Date(trialStartDate);
        trialEnd.setDate(trialEnd.getDate() + 7);
        const remaining = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));
        return Math.max(0, remaining);
    }, [isInTrial, trialStartDate]);

    const canStartTrial = useCallback(() => {
        return !trialUsed && SUBSCRIPTION_TIERS.PRO.trial;
    }, [trialUsed]);

    // Check if payment method is required for trial
    const requiresPaymentForTrial = useCallback(() => {
        return canStartTrial() && !hasPaymentMethod;
    }, [canStartTrial, hasPaymentMethod]);

    const startTrial = useCallback((paymentMethodProvided = false) => {
        /**
         * ⚠️ SECURITY: This function does NOT grant subscription access.
         * It only validates eligibility and returns instructions to proceed with Stripe.
         * Actual subscription activation happens via Stripe webhook.
         */
        if (!canStartTrial()) {
            return { success: false, error: 'Trial already used' };
        }

        // If payment method not saved and not being provided now, require it
        if (!hasPaymentMethod && !paymentMethodProvided) {
            return {
                success: false,
                error: 'payment_required',
                message: 'Payment method required for trial',
                requiresCheckout: true
            };
        }

        // Mark payment method as saved if provided
        if (paymentMethodProvided) {
            setHasPaymentMethod(true);
        }

        // ⚠️ IMPORTANT: We do NOT set currentTier here!
        // The tier will be set when Stripe webhook confirms the subscription.
        // We only mark trial intent and return success to proceed to checkout.
        console.log('[SubscriptionContext] Trial eligibility confirmed. User must complete Stripe Checkout.');

        return {
            success: true,
            requiresCheckout: true,
            message: 'Please complete checkout to start your free trial'
        };
    }, [canStartTrial, hasPaymentMethod]);

    // Get trial end date
    const getTrialEndDate = useCallback(() => {
        if (!trialStartDate) return null;
        const endDate = new Date(trialStartDate);
        endDate.setDate(endDate.getDate() + 7);
        return endDate;
    }, [trialStartDate]);

    // Check if we need to send trial notifications
    const checkTrialNotifications = useCallback(() => {
        if (!isInTrial()) return null;
        const daysRemaining = getTrialDaysRemaining();

        if (daysRemaining <= 3 && !trialNotifications.threeDaySent) {
            return { type: '3_day', message: 'Your trial ends in 3 days. Keep your Pro benefits!' };
        }
        if (daysRemaining <= 1 && !trialNotifications.oneDaySent) {
            return { type: '1_day', message: 'Your trial ends tomorrow! You will be charged unless you cancel.' };
        }
        return null;
    }, [isInTrial, getTrialDaysRemaining, trialNotifications]);

    // Mark notification as sent
    const markTrialNotificationSent = useCallback((type) => {
        setTrialNotifications(prev => ({
            ...prev,
            threeDaySent: type === '3_day' ? true : prev.threeDaySent,
            oneDaySent: type === '1_day' ? true : prev.oneDaySent
        }));
    }, []);

    // Check trial expiration and handle auto-conversion or downgrade
    const checkTrialExpiration = useCallback(() => {
        if (!trialStartDate) return;

        const trialEnd = new Date(trialStartDate);
        trialEnd.setDate(trialEnd.getDate() + 7);

        if (new Date() >= trialEnd && currentTier === 'PRO') {
            // Trial expired
            if (hasPaymentMethod) {
                // NOTE: Stripe webhooks automatically handle subscription charging
                // The webhook.js endpoint processes invoice.payment_succeeded events
                // and updates user tier in Supabase. Local state extends subscription.
                const newExpiry = new Date();
                if (billingCycle === 'yearly') {
                    newExpiry.setFullYear(newExpiry.getFullYear() + 1);
                } else {
                    newExpiry.setMonth(newExpiry.getMonth() + 1);
                }
                setExpiryDate(newExpiry);
                return { action: 'subscribed', message: 'Your Pro subscription is now active!' };
            } else {
                // No payment method, downgrade to Starter (except super admin)
                if (!isSuperAdmin) {
                    setCurrentTier('STARTER');
                    setExpiryDate(null);
                    return { action: 'downgraded', message: 'Your trial has ended. Subscribe to continue Pro features.' };
                }
                // Super admin stays ELITE
                return null;
            }
        }
        return null;
    }, [trialStartDate, currentTier, hasPaymentMethod, billingCycle]);

    // ==================== FEATURE GATES ====================

    // Scanning - handles both daily and total limits
    const canScan = useCallback(() => {
        // Super admin can always scan
        if (isSuperAdmin) return true;

        const limits = getTL(currentTier);
        // Check total limit for Starter tier
        if (limits.totalScans !== undefined && limits.totalScans !== Infinity) {
            if (totalScansUsed >= limits.totalScans) return false;
        }
        // Check daily limit (if applicable)
        if (limits.dailyScans !== Infinity && scansToday >= limits.dailyScans) {
            return false;
        }
        return true;
    }, [currentTier, scansToday, totalScansUsed]);

    const canAccessAIRefinement = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).aiRefinement;
    }, [currentTier, isSuperAdmin]);

    // Dashboard Features
    const canAccessMacroRings = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).macroRings;
    }, [currentTier, isSuperAdmin]);

    const canAccessWeightTrendChart = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).weightTrendChart;
    }, [currentTier, isSuperAdmin]);

    // Premium Features (Elite only)
    const canAccessMetabolicTable = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).metabolicTable;
    }, [currentTier, isSuperAdmin]);

    const canAccessHourlyBurnTimeline = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).hourlyBurnTimeline;
    }, [currentTier, isSuperAdmin]);

    // AI Features (Elite only)
    const canAccessAISchedule = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).aiSchedule;
    }, [currentTier, isSuperAdmin]);

    const canAccessAIWorkouts = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).aiWorkouts;
    }, [currentTier, isSuperAdmin]);

    const canAccessAIMealSuggestions = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).aiMealSuggestions;
    }, [currentTier, isSuperAdmin]);

    const canAccessAICoaching = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).aiCoaching;
    }, [currentTier, isSuperAdmin]);

    // Recipes
    const canAccessRecipes = useCallback(() => {
        if (isSuperAdmin) return true;
        const limits = getTL(currentTier);
        if (limits.recipesPerMonth === 0) return false;
        return limits.recipesPerMonth === Infinity || recipesThisMonth < limits.recipesPerMonth;
    }, [currentTier, recipesThisMonth, isSuperAdmin]);

    // Profile
    const hasVerifiedBadge = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).verifiedBadge;
    }, [currentTier, isSuperAdmin]);

    const hasAdsRemoved = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).removeAds;
    }, [currentTier, isSuperAdmin]);

    const hasPrioritySupport = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).prioritySupport;
    }, [currentTier, isSuperAdmin]);

    // ==================== USAGE TRACKING ====================

    const getRemainingScans = useCallback(() => {
        const limits = getTL(currentTier);
        // For Starter tier, use total limit (28 total ever)
        if (limits.totalScans !== undefined && limits.totalScans !== Infinity) {
            return Math.max(0, limits.totalScans - totalScansUsed);
        }
        // For paid tiers, unlimited
        if (limits.dailyScans === Infinity) return Infinity;
        return Math.max(0, limits.dailyScans - scansToday);
    }, [currentTier, scansToday, totalScansUsed]);

    const getRemainingRecipes = useCallback(() => {
        const limits = getTL(currentTier);
        if (limits.recipesPerMonth === Infinity) return Infinity;
        if (limits.recipesPerMonth === 0) return 0;
        return Math.max(0, limits.recipesPerMonth - recipesThisMonth);
    }, [currentTier, recipesThisMonth]);

    const useScan = useCallback(() => {
        if (!canScan()) return false;
        setScansToday(prev => prev + 1);
        // Track total scans for Starter tier
        const limits = getTL(currentTier);
        if (limits.totalScans !== undefined && limits.totalScans !== Infinity) {
            setTotalScansUsed(prev => prev + 1);
        }
        return true;
    }, [canScan, currentTier]);

    const useRecipe = useCallback(() => {
        if (!canAccessRecipes()) return false;
        setRecipesThisMonth(prev => prev + 1);
        return true;
    }, [canAccessRecipes]);


    // Check if user can earn from referrals (Starter cannot)
    const canEarnReferrals = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).canEarnReferrals;
    }, [currentTier, isSuperAdmin]);

    // ==================== AI GENERATION TRACKING ====================

    // AI generation - handles both weekly limits (Plus) and total limits (Starter)
    const canUseAIGeneration = useCallback(() => {
        const limits = getTL(currentTier);
        // Check total limit for Starter tier (28 total)
        if (limits.totalAIGenerations !== undefined && limits.totalAIGenerations !== Infinity) {
            if (totalAIGenerationsUsed >= limits.totalAIGenerations) return false;
        }
        // Check weekly limit for Pro tier (21/week)
        if (limits.weeklyAIGenerations !== Infinity && limits.weeklyAIGenerations !== undefined) {
            if (aiGenerationsThisWeek >= limits.weeklyAIGenerations) return false;
        }
        return true;
    }, [currentTier, aiGenerationsThisWeek, totalAIGenerationsUsed]);

    const getRemainingAIGenerations = useCallback(() => {
        const limits = getTL(currentTier);
        // For Starter tier, use total limit
        if (limits.totalAIGenerations !== undefined && limits.totalAIGenerations !== Infinity) {
            return Math.max(0, limits.totalAIGenerations - totalAIGenerationsUsed);
        }
        // For Pro tier, use weekly limit
        if (limits.weeklyAIGenerations !== Infinity && limits.weeklyAIGenerations !== undefined) {
            return Math.max(0, limits.weeklyAIGenerations - aiGenerationsThisWeek);
        }
        // For Elite, unlimited
        return Infinity;
    }, [currentTier, aiGenerationsThisWeek, totalAIGenerationsUsed]);

    const useAIGeneration = useCallback(() => {
        if (!canUseAIGeneration()) return false;
        // Track weekly for Pro tier
        setAiGenerationsThisWeek(prev => prev + 1);
        // Track total for Starter tier
        const limits = getTL(currentTier);
        if (limits.totalAIGenerations !== undefined && limits.totalAIGenerations !== Infinity) {
            setTotalAIGenerationsUsed(prev => prev + 1);
        }
        return true;
    }, [canUseAIGeneration, currentTier]);

    // ==================== EXERCISE GENERATION TRACKING ====================

    // Exercise generation - handles total limits for Starter tier (28 total)
    const canGenerateExercise = useCallback(() => {
        const limits = getTL(currentTier);
        // Check total limit for Starter tier
        if (limits.totalExerciseGeneration !== undefined && limits.totalExerciseGeneration !== Infinity) {
            if (totalExerciseGenerationsUsed >= limits.totalExerciseGeneration) return false;
        }
        // Check daily limit (if applicable)
        if (limits.dailyExerciseGeneration !== Infinity) {
            // For paid tiers, no daily limit enforced
            return true;
        }
        return true;
    }, [currentTier, totalExerciseGenerationsUsed]);

    const getRemainingExerciseGenerations = useCallback(() => {
        const limits = getTL(currentTier);
        // For Starter tier, use total limit (28 total ever)
        if (limits.totalExerciseGeneration !== undefined && limits.totalExerciseGeneration !== Infinity) {
            return Math.max(0, limits.totalExerciseGeneration - totalExerciseGenerationsUsed);
        }
        // For paid tiers, unlimited
        return Infinity;
    }, [currentTier, totalExerciseGenerationsUsed]);

    const useExerciseGeneration = useCallback(() => {
        if (!canGenerateExercise()) return false;
        // Track total exercise generations for Starter tier
        const limits = getTL(currentTier);
        if (limits.totalExerciseGeneration !== undefined && limits.totalExerciseGeneration !== Infinity) {
            setTotalExerciseGenerationsUsed(prev => prev + 1);
        }
        return true;
    }, [canGenerateExercise, currentTier]);

    // ==================== MEAL/EXERCISE LOG LIMITS ====================

    // Meal logging - STARTER: 28 TOTAL, PLUS/ELITE: unlimited
    const canLogMeal = useCallback(() => {
        const limits = getTL(currentTier);
        // Check total limit for Starter tier
        if (limits.totalMealLogs !== undefined && limits.totalMealLogs !== Infinity) {
            if (totalMealLogsUsed >= limits.totalMealLogs) return false;
        }
        // Check daily limit (if applicable)
        if (limits.maxMealLogsPerDay !== Infinity && mealLogsToday >= limits.maxMealLogsPerDay) {
            return false;
        }
        return true;
    }, [currentTier, mealLogsToday, totalMealLogsUsed]);

    const getRemainingMealLogs = useCallback(() => {
        const limits = getTL(currentTier);
        // For Starter tier, use total limit
        if (limits.totalMealLogs !== undefined && limits.totalMealLogs !== Infinity) {
            return Math.max(0, limits.totalMealLogs - totalMealLogsUsed);
        }
        // For paid tiers, unlimited
        if (limits.maxMealLogsPerDay === Infinity) return Infinity;
        return Math.max(0, limits.maxMealLogsPerDay - mealLogsToday);
    }, [currentTier, mealLogsToday, totalMealLogsUsed]);

    const useMealLog = useCallback(() => {
        if (!canLogMeal()) return false;
        setMealLogsToday(prev => prev + 1);
        // Track total for Starter tier
        const limits = getTL(currentTier);
        if (limits.totalMealLogs !== undefined && limits.totalMealLogs !== Infinity) {
            setTotalMealLogsUsed(prev => prev + 1);
        }
        return true;
    }, [canLogMeal, currentTier]);

    // Exercise logging - STARTER: 28 TOTAL, PLUS/ELITE: unlimited
    const canLogExercise = useCallback(() => {
        const limits = getTL(currentTier);
        // Check total limit for Starter tier
        if (limits.totalExerciseLogs !== undefined && limits.totalExerciseLogs !== Infinity) {
            if (totalExerciseLogsUsed >= limits.totalExerciseLogs) return false;
        }
        // Check daily limit (if applicable)
        if (limits.maxExerciseLogsPerDay !== Infinity && exerciseLogsToday >= limits.maxExerciseLogsPerDay) {
            return false;
        }
        return true;
    }, [currentTier, exerciseLogsToday, totalExerciseLogsUsed]);

    const getRemainingExerciseLogs = useCallback(() => {
        const limits = getTL(currentTier);
        // For Starter tier, use total limit
        if (limits.totalExerciseLogs !== undefined && limits.totalExerciseLogs !== Infinity) {
            return Math.max(0, limits.totalExerciseLogs - totalExerciseLogsUsed);
        }
        // For paid tiers, unlimited
        if (limits.maxExerciseLogsPerDay === Infinity) return Infinity;
        return Math.max(0, limits.maxExerciseLogsPerDay - exerciseLogsToday);
    }, [currentTier, exerciseLogsToday, totalExerciseLogsUsed]);

    const useExerciseLog = useCallback(() => {
        if (!canLogExercise()) return false;
        setExerciseLogsToday(prev => prev + 1);
        // Track total for Starter tier
        const limits = getTL(currentTier);
        if (limits.totalExerciseLogs !== undefined && limits.totalExerciseLogs !== Infinity) {
            setTotalExerciseLogsUsed(prev => prev + 1);
        }
        return true;
    }, [canLogExercise, currentTier]);

    // ==================== FUSION & REFINEMENT LIMITS ====================

    // Fusion - STARTER: 28 TOTAL, PLUS/ELITE: unlimited
    const canUseFusionWithLimit = useCallback(() => {
        const limits = getTL(currentTier);
        if (!limits.canUseFusion) return false;
        // Check total limit for Starter tier
        if (limits.totalFusionLimit !== undefined && limits.totalFusionLimit !== Infinity) {
            if (totalFusionsUsed >= limits.totalFusionLimit) return false;
        }
        return true;
    }, [currentTier, totalFusionsUsed]);

    const getRemainingFusions = useCallback(() => {
        const limits = getTL(currentTier);
        if (!limits.canUseFusion) return 0;
        if (limits.totalFusionLimit !== undefined && limits.totalFusionLimit !== Infinity) {
            return Math.max(0, limits.totalFusionLimit - totalFusionsUsed);
        }
        return Infinity;
    }, [currentTier, totalFusionsUsed]);

    const useFusion = useCallback(() => {
        if (!canUseFusionWithLimit()) return false;
        const limits = getTL(currentTier);
        if (limits.totalFusionLimit !== undefined && limits.totalFusionLimit !== Infinity) {
            setTotalFusionsUsed(prev => prev + 1);
        }
        return true;
    }, [canUseFusionWithLimit, currentTier]);

    // Refinement - STARTER: 28 TOTAL, PLUS/ELITE: unlimited
    const canUseRefinement = useCallback(() => {
        const limits = getTL(currentTier);
        // Check total limit for Starter tier
        if (limits.totalRefinements !== undefined && limits.totalRefinements !== Infinity) {
            if (totalRefinementsUsed >= limits.totalRefinements) return false;
        }
        // Also check if aiRefinement is enabled for the tier
        return limits.aiRefinement !== false || currentTier !== 'STARTER';
    }, [currentTier, totalRefinementsUsed]);

    const getRemainingRefinements = useCallback(() => {
        const limits = getTL(currentTier);
        if (limits.totalRefinements !== undefined && limits.totalRefinements !== Infinity) {
            return Math.max(0, limits.totalRefinements - totalRefinementsUsed);
        }
        return Infinity;
    }, [currentTier, totalRefinementsUsed]);

    const useRefinement = useCallback(() => {
        if (!canUseRefinement()) return false;
        const limits = getTL(currentTier);
        if (limits.totalRefinements !== undefined && limits.totalRefinements !== Infinity) {
            setTotalRefinementsUsed(prev => prev + 1);
        }
        return true;
    }, [canUseRefinement, currentTier]);

    // ==================== FEATURE GATES ====================

    const canUseFusion = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).canUseFusion;
    }, [currentTier, isSuperAdmin]);
    const canSaveMeals = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).canSaveMeals;
    }, [currentTier, isSuperAdmin]);
    const canSaveExercises = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).canSaveExercises;
    }, [currentTier, isSuperAdmin]);
    const canCreateRoutine = useCallback(() => {
        if (isSuperAdmin) return true;
        return getTL(currentTier).canCreateRoutine;
    }, [currentTier, isSuperAdmin]);

    // ==================== SUBSCRIPTION MANAGEMENT ====================

    /**
     * refreshTierFromRevenueCat — Apple IAP Post-Purchase State Refresh
     *
     * Call this immediately after:
     *   a) RevenueCat paywall returns PURCHASED or RESTORED
     *   b) addEntitlementListener fires with a new tier
     *
     * This is the single fix for Guideline 2.1(b): after a successful sandbox purchase
     * the app must immediately reflect the new subscription state without requiring
     * a restart. RevenueCat is the authoritative source of truth for IAP entitlements.
     *
     * @returns {Promise<string>} The resolved tier ('PRO' | 'STARTER')
     */
    const refreshTierFromRevenueCat = useCallback(async () => {
        if (isSuperAdmin) {
            console.log('[SubscriptionContext] 👑 SuperAdmin — skipping RevenueCat refresh');
            return 'PRO';
        }

        try {
            const { checkEntitlements } = await import('../services/iapService');
            const { tier, expiryDate: rcExpiry } = await checkEntitlements();

            console.log('[SubscriptionContext] ⚡ RevenueCat entitlement refresh → tier:', tier);

            // 1. Update React state immediately (no delay required)
            setCurrentTier(tier);

            // 2. Persist to user-specific localStorage key
            if (currentUserEmail) {
                localStorage.setItem(`calobite_tier_${currentUserEmail}`, tier);
            }
            localStorage.setItem('calobite_subscription_tier', tier);

            // 3. Update expiry if available
            if (rcExpiry) {
                setExpiryDate(new Date(rcExpiry));
            }

            // 4. Write to Supabase so the next cold-boot Supabase sync is correct
            //    (the RevenueCat webhook will also do this, but may be delayed in sandbox)
            if (currentUserEmail && tier === 'PRO') {
                import('../lib/supabase').then(({ supabase }) => {
                    if (!supabase) return;
                    supabase
                        .from('profiles')
                        .update({
                            subscription_tier: 'PRO',
                            subscription_expiry: rcExpiry || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
                        })
                        .eq('email', currentUserEmail.toLowerCase())
                        .then(({ error }) => {
                            if (error) console.warn('[SubscriptionContext] Supabase tier update skipped:', error.message);
                            else console.log('[SubscriptionContext] ✅ Supabase tier updated to PRO');
                        });
                }).catch(() => { });
            }

            return tier;
        } catch (err) {
            console.error('[SubscriptionContext] RevenueCat refresh failed:', err);
            return currentTier;
        }
    }, [isSuperAdmin, currentUserEmail, currentTier]);

    // ── RevenueCat native entitlement event listener ───────────────────────
    // App.jsx wires up addEntitlementListener and re-broadcasts via this custom event.
    // This covers purchases/restores that are confirmed directly by the OS.
    useEffect(() => {
        const handleEntitlementChange = async (event) => {
            const { tier } = event.detail || {};
            if (!tier) return;
            console.log('[SubscriptionContext] 📡 calobite_entitlement_changed received → tier:', tier);
            await refreshTierFromRevenueCat();
        };
        window.addEventListener('calobite_entitlement_changed', handleEntitlementChange);
        return () => window.removeEventListener('calobite_entitlement_changed', handleEntitlementChange);
    }, [refreshTierFromRevenueCat]);

    const presentPaywall = useCallback(async () => {
        try {
            console.log('[SubscriptionContext] Presenting native RevenueCat paywall...');
            const iap = await import('../services/iapService');
            const result = await iap.presentPaywall();

            // ⚡ CRITICAL FIX (Guideline 2.1b): Immediately refresh entitlement state
            // so the UI reflects Pro status right after payment — no restart needed.
            // Uses refreshTierFromRevenueCat which also syncs to Supabase.
            if (result?.purchased) {
                console.log('[SubscriptionContext] Paywall returned purchased — refreshing tier immediately');
                await refreshTierFromRevenueCat();
            }

            return result;
        } catch (error) {
            console.error('[SubscriptionContext] Error presenting paywall:', error);
            throw error;
        }
    }, [currentUserEmail, refreshTierFromRevenueCat]);

    const upgradeTier = useCallback((newTier, isYearly = true) => {
        /**
         * ⚠️ SECURITY: This function DOES NOT grant subscription access.
         * It is only used to:
         * 1. Trigger Stripe checkout redirect
         * 2. Handle post-webhook UI updates from fetchSubscriptionStatus()
         * 
         * Actual tier changes happen via:
         * - User completes Stripe Checkout
         * - Stripe webhook (checkout.session.completed) updates database
         * - fetchSubscriptionStatus() reads database and updates UI
         */
        const isUpgrade = SUBSCRIPTION_TIERS[newTier]?.level > SUBSCRIPTION_TIERS[currentTier]?.level;

        console.log(`[SubscriptionContext] Upgrade requested: ${currentTier} -> ${newTier} (${isYearly ? 'yearly' : 'monthly'})`);
        console.log('⚠️ User must complete Stripe Checkout. Tier will update after webhook confirmation.');

        // Return intent for Stripe checkout - DO NOT set tier here
        return {
            success: true,
            requiresCheckout: true,
            type: isUpgrade ? 'upgrade' : 'new',
            tier: newTier,
            billing: isYearly ? 'yearly' : 'monthly',
            message: 'Please complete payment to activate your subscription'
        };
    }, [currentTier, canStartTrial]);



    const cancelSubscription = useCallback(() => {
        // Downgrade to Starter at end of billing period
        // In real app, this would schedule cancellation
        return {
            success: true,
            message: 'Subscription will end at billing period',
            expiryDate
        };
    }, [expiryDate]);

    const isSubscriptionActive = useCallback(() => {
        // Super admin always has active subscription
        if (isSuperAdmin) return true;
        // STARTER tier is always "active" (it's the free plan)
        if (currentTier === 'STARTER') return true;
        // PRO tier: active even if expiry not yet fetched (e.g. just purchased)
        if (currentTier === 'PRO') {
            if (!expiryDate) return true; // Expiry not fetched yet — trust the tier
            return new Date() < new Date(expiryDate);
        }
        return false;
    }, [currentTier, expiryDate, isSuperAdmin]);

    const getDaysUntilExpiry = useCallback(() => {
        // Super admin never expires
        if (isSuperAdmin) return Infinity;
        if (!expiryDate || currentTier === 'STARTER') return null;
        const diff = new Date(expiryDate) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }, [expiryDate, currentTier, isSuperAdmin]);

    // ==================== UPSELL MESSAGING ====================

    const getUpsellMessage = useCallback((feature) => {
        const messages = {
            scan: {
                title: "Scan Limit Reached! 📸",
                message: "Upgrade to Pro for unlimited scans",
                requiredTier: 'PRO',
                cta: "Get Unlimited Scans"
            },
            macroRings: {
                title: "Track Your Macros 💪",
                message: "See protein, carbs & fat breakdown with Pro",
                requiredTier: 'PRO',
                cta: "Unlock Macro Tracking"
            },
            metabolicTable: {
                title: "Unlock Your Body Clock 🔥",
                message: "See WHEN you burn most calories - Pro exclusive",
                requiredTier: 'PRO',
                cta: "Upgrade to Pro"
            },
            aiSchedule: {
                title: "AI-Powered Schedule ⚡",
                message: "Get personalized meal timing optimized for YOUR metabolism",
                requiredTier: 'PRO',
                cta: "Upgrade to Pro"
            },
            aiWorkouts: {
                title: "Smart Workout Generation 🏋️",
                message: "AI creates workouts matched to your goals",
                requiredTier: 'PRO',
                cta: "Upgrade to Pro"
            },
            weightChart: {
                title: "Visualize Your Progress 📊",
                message: "Track weight trends over time with beautiful charts",
                requiredTier: 'PRO',
                cta: "Unlock Weight Charts"
            },
            aiRefinement: {
                title: "Smart Food Detection 🤖",
                message: "AI helps identify and refine your food entries",
                requiredTier: 'PRO',
                cta: "Unlock AI Features"
            }
        };
        return messages[feature] || {
            title: "Premium Feature 👑",
            message: "Upgrade to access",
            requiredTier: 'PRO',
            cta: "Upgrade Now"
        };
    }, []);

    // ==================== PRICING HELPERS ====================

    const getPrice = useCallback((tier, isYearly) => {
        const tierInfo = SUBSCRIPTION_TIERS[tier];
        if (!tierInfo) return 0;
        return isYearly ? tierInfo.yearlyPrice : tierInfo.monthlyPrice;
    }, []);

    const getMonthlyEquivalent = useCallback((tier) => {
        const tierInfo = SUBSCRIPTION_TIERS[tier];
        if (!tierInfo || !tierInfo.yearlyPrice) return 0;
        return (tierInfo.yearlyPrice / 12).toFixed(2);
    }, []);

    const getSavingsPercent = useCallback((tier) => {
        const tierInfo = SUBSCRIPTION_TIERS[tier];
        return tierInfo?.savings || null;
    }, []);


    // ==================== REFERRAL FUNCTIONS ====================
    // Note: applyReferralCode is defined earlier with discount logic

    const getReferralCommissionRate = useCallback(() => {
        if (activeReferrals <= 0) return 0;
        if (activeReferrals <= 5) return 5;
        if (activeReferrals <= 20) return 10;
        if (activeReferrals <= 100) return 15;
        return 20;
    }, [activeReferrals]);

    const incrementActiveReferral = useCallback(() => {
        setActiveReferrals(prev => prev + 1);
    }, []);

    const decrementActiveReferral = useCallback(() => {
        setActiveReferrals(prev => Math.max(0, prev - 1));
    }, []);

    // ==================== CONTEXT VALUE ====================

    const value = {
        // Super Admin
        isSuperAdmin,

        // State
        currentTier,
        expiryDate,
        scansToday,
        recipesThisMonth,
        billingCycle,
        trialStartDate,
        trialUsed,

        // Referral state
        referredBy,
        activeReferrals,
        referralEarnings,
        pendingEarnings,
        userReferralCode,

        // Tier info
        getTierInfo,
        getTierLimits,
        hasTierLevel,
        TIERS: SUBSCRIPTION_TIERS,
        PRODUCTS: BILLING_PRODUCTS,
        CURRENCY,
        CURRENCY_SYMBOL,

        // Trial management
        isInTrial,
        getTrialDaysRemaining,
        canStartTrial,
        startTrial,
        getTrialEndDate,
        requiresPaymentForTrial,
        checkTrialNotifications,
        markTrialNotificationSent,
        checkTrialExpiration,

        // Payment method
        hasPaymentMethod,
        setHasPaymentMethod,

        // Scheduled downgrade
        scheduledDowngrade,
        setScheduledDowngrade,

        // Feature gates - Scanning
        canScan,
        canAccessAIRefinement,

        // Feature gates - Dashboard
        canAccessMacroRings,
        canAccessWeightTrendChart,

        // Feature gates - Premium (Elite)
        canAccessMetabolicTable,
        canAccessHourlyBurnTimeline,

        // Feature gates - AI (Elite)
        canAccessAISchedule,
        canAccessAIWorkouts,
        canAccessAIMealSuggestions,
        canAccessAICoaching,

        // Feature gates - Other
        canAccessRecipes,
        hasVerifiedBadge,
        hasAdsRemoved,
        hasPrioritySupport,

        // Usage tracking
        getRemainingScans,
        getRemainingRecipes,
        useScan,
        useRecipe,

        canEarnReferrals,

        // AI generation tracking
        aiGenerationsThisWeek,
        canUseAIGeneration,
        getRemainingAIGenerations,
        useAIGeneration,

        // Exercise generation tracking
        totalExerciseGenerationsUsed,
        canGenerateExercise,
        getRemainingExerciseGenerations,
        useExerciseGeneration,

        // Daily meal/exercise log tracking
        mealLogsToday,
        exerciseLogsToday,
        totalMealLogsUsed,
        totalExerciseLogsUsed,
        canLogMeal,
        canLogExercise,
        getRemainingMealLogs,
        getRemainingExerciseLogs,
        useMealLog,
        useExerciseLog,

        // Fusion tracking (STARTER: 28 total)
        totalFusionsUsed,
        canUseFusionWithLimit,
        getRemainingFusions,
        useFusion,

        // Refinement tracking (STARTER: 28 total)
        totalRefinementsUsed,
        canUseRefinement,
        getRemainingRefinements,
        useRefinement,

        // Feature gates - New
        canUseFusion,
        canSaveMeals,
        canSaveExercises,
        canCreateRoutine,

        // Subscription management
        presentPaywall,
        upgradeTier,
        refreshTierFromRevenueCat,
        cancelSubscription,
        isSubscriptionActive,
        getDaysUntilExpiry,
        getUpsellMessage,

        // Pricing helpers
        getPrice,
        getMonthlyEquivalent,
        getSavingsPercent,

        // Referral functions (tracking only — discounts handled by App Store)
        applyReferralCode,
        getReferralCommissionRate,
        incrementActiveReferral,
        decrementActiveReferral,

        // Production sync state (for error handling UI)
        syncLoading,
        syncError
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
